/**
 * SEO, AEO, and marketing content for every free tool page.
 * Used by FreeToolsPage for meta tags, headings, FAQ, and structured data.
 */

const BASE_URL = 'https://focuzon.com';

export interface ToolFAQ {
  question: string;
  answer: string;
}

export interface ToolSEOConfig {
  title: string;
  metaDescription: string;
  ogTitle: string;
  ogDescription: string;
  h1: string;
  subtitle: string;
  /** H2 above the tool UI, e.g. "Create Your Invoice" */
  toolSectionTitle?: string;
  whatIs: string;
  howToSteps: string[];
  features: string[];
  benefits: string;
  faqs: ToolFAQ[];
  /** Optional: for HowTo schema (step name + text) */
  howToSchemaSteps?: { name: string; text: string }[];
}

type ToolId = string;

function faqSchema(mainEntity: { '@type': string; name: string; acceptedAnswer: { '@type': string; text: string } }[]) {
  return { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity };
}

function softwareApplicationSchema(toolId: string, name: string, description: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name,
    description,
    url: `${BASE_URL}/tools/${toolId}`,
    applicationCategory: 'UtilitiesApplication',
    operatingSystem: 'Any',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
  };
}

function howToSchema(name: string, steps: { name: string; text: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    step: steps.map((s, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };
}

const SEO: Record<ToolId, ToolSEOConfig> = {
  'password-generator': {
    title: 'Secure Password Generator Online | Focuzon',
    metaDescription:
      'Generate strong, secure passwords instantly. Free password generator with length and character options. No signup. Use for accounts and apps.',
    ogTitle: 'Free Password Generator | Focuzon',
    ogDescription: 'Create strong, secure passwords in one click. Free and no signup.',
    h1: 'Free Password Generator',
    subtitle: 'Generate secure random passwords with one click.',
    whatIs:
      'A password generator creates random, strong passwords so you don’t have to think them up yourself. You choose length and whether to include uppercase, numbers, and symbols, then copy the result.',
    howToSteps: [
      'Set the password length (e.g. 12–32 characters)',
      'Choose options: uppercase, lowercase, numbers, symbols',
      'Click generate and copy the password',
      'Use it for your account or app',
    ],
    features: [
      'Adjustable length',
      'Uppercase, lowercase, numbers, and symbols',
      'One-click copy',
      'Runs in your browser; nothing is stored',
      'No signup required',
    ],
    benefits:
      'Strong, unique passwords reduce the risk of account breaches. A generator makes it easy to use a different password for every service.',
    faqs: [
      {
        question: 'What is a password generator?',
        answer:
          'A password generator creates random, strong passwords so you do not have to think them up yourself. You set the length and choose character types (uppercase, lowercase, numbers, symbols), then get a secure password to use for your accounts. Strong passwords reduce breach risk. Use a different password for each service. Focuzon’s generator runs in your browser with no signup.',
      },
      {
        question: 'Is this password generator safe?',
        answer:
          'Yes. The generator runs entirely in your browser. Passwords are created locally using secure random algorithms and are not sent to or stored on any server. Nothing is logged or transmitted. You can verify by disconnecting the internet and generating; it will still work. This approach is recommended by security experts for password generation.',
      },
      {
        question: 'Is it free?',
        answer: 'Yes. The password generator is free to use with no signup or limits. No credit card, subscription, or account is required. You can generate as many passwords as you need. The tool works immediately in your browser. Focuzon offers it as part of a suite of free productivity tools.',
      },
      {
        question: 'How long should my password be?',
        answer: 'For most accounts, 12–16 characters with mixed letters, numbers, and symbols is recommended. Longer passwords (16–24 characters) are better for sensitive accounts like email or banking. Avoid dictionary words and personal information. Mix character types to increase strength. This generator lets you set any length from 8 to 32 or more.',
      },
      {
        question: 'Can I use special characters?',
        answer: 'Yes. You can include symbols such as !@#$%^&* to make passwords stronger. Turn on the symbols option when generating. Special characters increase the possible combinations and make brute-force attacks harder. Many sites require at least one symbol. Focuzon’s generator includes common symbols that work across most services.',
      },
      {
        question: 'How do I generate a strong password?',
        answer: 'Set the length to at least 12–16 characters, enable uppercase, lowercase, numbers, and symbols, then click generate. Copy the result and paste it into your account or password manager. Do not reuse passwords across sites. Use a password manager to store them securely. This generator gives you a strong password in one click.',
      },
    ],
    howToSchemaSteps: [
      { name: 'Set length', text: 'Set the password length (e.g. 16 characters).' },
      { name: 'Choose options', text: 'Choose uppercase, lowercase, numbers, and symbols.' },
      { name: 'Generate and copy', text: 'Click generate and copy the password to use.' },
    ],
  },
  'qr-code': {
    title: 'Free QR Code Generator – Create QR Codes Online | Focuzon',
    metaDescription:
      'Generate QR codes for URLs, text, and more. Free QR code generator. No signup. Download as PNG. Use for links, WiFi, or contact info.',
    ogTitle: 'Free QR Code Generator | Focuzon',
    ogDescription: 'Create QR codes for URLs and text. Free and no signup.',
    h1: 'Free QR Code Generator',
    subtitle: 'Generate QR codes for URLs, text, and more.',
    whatIs:
      'A QR code generator turns a URL or text into a scannable QR code. You paste a link or text, and the tool creates a code that anyone can scan with their phone to open the link or see the content.',
    howToSteps: [
      'Paste a URL or enter text',
      'The QR code is generated automatically',
      'Download the image as PNG',
      'Use it on flyers, screens, or documents',
    ],
    features: [
      'Works with URLs and plain text',
      'Instant generation',
      'Download as PNG',
      'No signup required',
      'Free to use',
    ],
    benefits:
      'QR codes make it easy to share links or information without typing. Use them for menus, WiFi, events, or contact details.',
    faqs: [
      {
        question: 'What is a QR code generator?',
        answer:
          'A QR code generator creates a scannable image from a URL or text. You paste a link or enter content, and the tool produces a QR code. When someone scans it with their phone camera, they open the link or see the text. QR codes are useful for menus, WiFi credentials, event registration, and contact sharing. No app is needed to scan; most phones support it natively.',
      },
      {
        question: 'Is the QR code generator free?',
        answer: 'Yes. You can generate and download QR codes for free with no signup. No credit card or account is required. Create as many codes as you need. The tool works in your browser. Focuzon offers it as part of free productivity tools. Download as PNG and use on flyers, screens, or documents.',
      },
      {
        question: 'What can I put in a QR code?',
        answer: 'You can use any URL (website link) or plain text. URLs work best for opening websites, forms, or apps. Plain text works for WiFi passwords, contact info, or short messages. Many phones scan and open links automatically. Avoid extremely long URLs; use a shortener if needed. Test your QR code before printing or sharing.',
      },
      {
        question: 'Can I download the QR code?',
        answer: 'Yes. You can download the QR code as a PNG image and use it anywhere. PNG works for print and digital use. Add it to flyers, presentations, business cards, or screens. Ensure sufficient contrast (dark on light) for reliable scanning. Test with your phone before mass production. No watermarks or limits on Focuzon’s free tool.',
      },
      {
        question: 'Do I need an account?',
        answer: 'No. The QR code generator works without an account or signup. Open the tool, paste your URL or text, and download. No email or login required. The tool runs in your browser. Focuzon keeps the experience simple so you can create QR codes quickly. Optional account creation is for other features like focus timers.',
      },
    ],
    howToSchemaSteps: [
      { name: 'Enter URL or text', text: 'Paste a URL or enter the text you want in the QR code.' },
      { name: 'Download', text: 'Download the QR code image as PNG.' },
    ],
  },
  'profit-margin': {
    title: 'Free Profit Margin Calculator – Calculate Margin & Markup | Focuzon',
    metaDescription:
      'Calculate profit margin and markup instantly. Free profit margin calculator with tax and quantity. Use for pricing and business planning. No signup.',
    ogTitle: 'Free Profit Margin Calculator | Focuzon',
    ogDescription: 'Calculate profit margin and markup. Free calculator with tax support.',
    h1: 'Profit Margin Calculator',
    subtitle: 'Calculate profit margin and markup with optional tax and quantity.',
    whatIs:
      'A profit margin calculator helps you work out margin and markup from revenue and cost. You can add tax and quantity to see per-unit figures. Useful for pricing and business decisions.',
    howToSteps: [
      'Enter revenue (selling price) and cost',
      'Add optional tax rate and quantity',
      'See profit margin and markup',
      'Use results for pricing or reports',
    ],
    features: [
      'Margin and markup calculation',
      'Optional tax percentage',
      'Per-unit view with quantity',
      'Multiple currencies',
      'No signup required',
    ],
    benefits:
      'Knowing your margin and markup helps you price correctly and understand profitability. This calculator gives you the numbers in seconds.',
    faqs: [
      {
        question: 'What is a profit margin calculator?',
        answer:
          'A profit margin calculator computes your profit margin and markup from revenue and cost. You enter selling price and cost, and the tool shows margin (profit as a share of revenue) and markup (percentage added to cost). You can add tax rate and quantity for per-unit figures. Useful for pricing, business planning, and profitability analysis. Focuzon’s calculator is free with no signup.',
      },
      {
        question: 'Is the profit margin calculator free?',
        answer: 'Yes. You can use it for free with no signup or limits. No credit card or account required. Calculate margin and markup as often as you need. The tool works in your browser. Focuzon offers it alongside other free productivity tools like password generator and currency converter. Results update instantly as you change inputs.',
      },
      {
        question: 'What is the difference between margin and markup?',
        answer:
          'Margin is profit as a percentage of revenue: (Revenue - Cost) / Revenue. Markup is the percentage added to cost: (Revenue - Cost) / Cost. A 50% margin equals 100% markup. Both are shown in the tool. Margin is common in retail; markup is useful for pricing from cost. Understanding both helps you set prices correctly.',
      },
      {
        question: 'Can I add tax?',
        answer: 'Yes. You can enter a tax rate to include tax in the calculations. This is helpful when you need to account for sales tax or VAT in your pricing. The tool shows how tax affects margin and markup. Enter the rate as a percentage. Combined with quantity, you get per-unit figures including tax.',
      },
      {
        question: 'Does it support multiple currencies?',
        answer: 'Yes. You can choose a currency and see formatted results in that currency. The math is the same regardless of currency; formatting adapts to your selection. Useful for international pricing or when comparing costs in different currencies. No conversion is performed; it is for display and consistency.',
      },
    ],
    howToSchemaSteps: [
      { name: 'Enter revenue and cost', text: 'Enter your selling price (revenue) and cost.' },
      { name: 'Add tax and quantity', text: 'Optionally add tax rate and quantity for per-unit view.' },
      { name: 'View results', text: 'See profit margin and markup calculated instantly.' },
    ],
  },
  'emi-calculator': {
    title: 'Free EMI Calculator – Loan EMI, SIP, Lump Sum & More | Focuzon',
    metaDescription:
      'Calculate loan EMI, SIP growth, lump sum investment, and flat rate loans. Free EMI calculator with eligibility. No signup required.',
    ogTitle: 'Free EMI Calculator | Focuzon',
    ogDescription: 'Loan EMI, SIP, lump sum, and flat rate. Free calculator with eligibility.',
    h1: 'EMI Calculator',
    subtitle: 'Loan EMI, SIP, lump sum, flat rate & eligibility.',
    whatIs:
      'An EMI calculator helps you estimate loan repayments (EMI), SIP growth, lump sum investment growth, or flat-rate loan EMI. You can also check loan eligibility from income and EMI share.',
    howToSteps: [
      'Choose mode: Loan EMI, SIP, Lump Sum, or Flat Rate Loan',
      'Enter principal, rate, and tenure',
      'See monthly EMI or future value',
      'Use eligibility mode to check loan amount from income',
    ],
    features: [
      'Loan EMI calculation',
      'SIP and lump sum investment growth',
      'Flat rate loan option',
      'Loan eligibility from income and EMI %',
      'Multiple currencies',
    ],
    benefits:
      'Planning loans or investments is easier with clear numbers. This tool gives you EMI, future value, or eligibility in one place.',
    faqs: [
      {
        question: 'What is an EMI calculator?',
        answer:
          'An EMI calculator computes your monthly loan payment (EMI) from loan amount, interest rate, and tenure. It can also show SIP growth, lump sum investment returns, or flat-rate loan EMI. Useful for home loans, personal loans, or investments. Enter principal, annual rate, and months to get results. Focuzon’s calculator supports multiple modes and currencies. Free with no signup.',
      },
      {
        question: 'Is the EMI calculator free?',
        answer: 'Yes. All modes (loan EMI, SIP, lump sum, flat rate) are free to use with no signup. No credit card or account required. Calculate as often as you need. The tool works in your browser. Focuzon offers it with other free productivity tools. Results update instantly. Optional eligibility mode shows how much loan you may get from income.',
      },
      {
        question: 'Can I calculate SIP growth?',
        answer: 'Yes. Switch to SIP mode and enter monthly investment amount, expected annual return rate, and tenure in months. The tool shows future value and wealth multiplier. Useful for planning mutual fund SIPs or recurring investments. Compounding is applied monthly. Compare different rates and tenures to plan your investment. Free with no signup.',
      },
      {
        question: 'What is loan eligibility?',
        answer: 'Eligibility mode estimates how much loan you can get based on your income and the share of income used for EMI. Lenders often cap EMI at 40–50% of income. Enter your monthly income and EMI percentage; the tool calculates maximum principal. Use it to check home loan or personal loan eligibility before applying. Results are indicative, not a guarantee.',
      },
      {
        question: 'Does it support different currencies?',
        answer: 'Yes. You can select a currency and see results in that currency. The math is the same; only the symbol and formatting change. Useful for loans or investments in INR, USD, EUR, or other currencies. No conversion is performed; it is for display. Enter amounts in your chosen currency for consistent results.',
      },
    ],
    howToSchemaSteps: [
      { name: 'Choose mode', text: 'Select Loan EMI, SIP, Lump Sum, or Flat Rate Loan.' },
      { name: 'Enter values', text: 'Enter principal, interest rate, and tenure.' },
      { name: 'View results', text: 'See monthly EMI or future value calculated.' },
    ],
  },
  'currency-converter': {
    title: 'Free Currency Converter – Live Exchange Rates (USD, INR, EUR) | Focuzon',
    metaDescription:
      'Convert currencies with live exchange rates. Free currency converter for USD to INR, EUR, GBP and more. No signup. Accurate and fast.',
    ogTitle: 'Free Currency Converter | Focuzon',
    ogDescription: 'Convert currencies with live rates. USD, INR, EUR, GBP and more.',
    h1: 'Currency Converter',
    subtitle: 'Convert between currencies with live exchange rates.',
    whatIs:
      'A currency converter lets you convert one currency into another using up-to-date exchange rates. You enter an amount and choose from and to currencies (e.g. USD to INR) to see the result.',
    howToSteps: [
      'Enter the amount you want to convert',
      'Select “from” and “to” currencies',
      'See the converted amount and today’s rate',
      'Rates are updated regularly',
    ],
    features: [
      'Live exchange rates',
      'Major currencies: USD, EUR, GBP, INR, JPY, AUD, CAD, CHF',
      'Clear display of today’s rate and date',
      'No signup required',
      'Works on desktop and mobile',
    ],
    benefits:
      'Converting currencies with current rates helps you plan travel, payments, or pricing. This tool gives you a quick, clear result.',
    faqs: [
      {
        question: 'What is a currency converter?',
        answer:
          'A currency converter turns an amount in one currency into another using exchange rates. You enter an amount, pick from and to currencies (e.g. USD to INR), and see the result. Useful for travel, international payments, or pricing. Focuzon\'s tool uses live or recent rates for accuracy. No signup required. Works on desktop and mobile.',
      },
      {
        question: 'Are the exchange rates live?',
        answer: 'Yes. The tool fetches current or recent exchange rates from trusted sources so you see up-to-date conversions. Rates change throughout the day; the tool reflects the latest available data. Check the date shown with the rate. For large transfers, verify with your bank. Focuzon updates rates regularly for accuracy.',
      },
      {
        question: 'Is the currency converter free?',
        answer: 'Yes. It’s free to use with no signup or limits.',
      },
      {
        question: 'Which currencies are supported?',
        answer: 'Supported currencies include USD, EUR, GBP, INR, JPY, AUD, CAD, and CHF. These cover most travel and business needs. Use the dropdowns to select from and to currencies. Direct pairs like USD to INR or EUR to GBP work. The tool shows today\'s rate and the converted amount.',
      },
      {
        question: 'Can I convert USD to INR?',
        answer: 'Yes. You can convert USD to INR and other major pairs. Use the from dropdown to select USD and the to dropdown for INR. Enter the amount and see the result with today\'s rate. The tool supports USD, EUR, GBP, INR, JPY, AUD, CAD, and CHF. No signup required. Free for personal and business use.',
      },
    ],
    howToSchemaSteps: [
      { name: 'Enter amount', text: 'Enter the amount you want to convert.' },
      { name: 'Select currencies', text: 'Choose from and to currencies (e.g. USD to INR).' },
      { name: 'View result', text: 'See the converted amount and today\'s exchange rate.' },
    ],
  },
  'word-counter': {
    title: 'Free Word Counter – Count Words, Characters & Sentences | Focuzon',
    metaDescription:
      'Count words, characters, sentences, and paragraphs instantly. Free word counter for essays, articles, and social media. No signup required.',
    ogTitle: 'Free Word Counter | Focuzon',
    ogDescription: 'Count words, characters, and sentences. Free and no signup.',
    h1: 'Word Counter',
    subtitle: 'Count words, characters, and sentences.',
    whatIs:
      'A word counter shows how many words, characters, sentences, and paragraphs are in your text. Useful for essays, articles, social posts, or any content with length limits.',
    howToSteps: [
      'Paste or type your text in the box',
      'See live counts for words, characters, sentences, paragraphs',
      'Use the numbers to meet length limits or goals',
    ],
    features: [
      'Word count',
      'Character count (with and without spaces)',
      'Sentence and paragraph count',
      'Updates as you type',
      'No signup required',
    ],
    benefits:
      'Knowing your word and character count helps you hit essay limits, tweet lengths, or content goals without guessing.',
    faqs: [
      {
        question: 'What is a word counter?',
        answer:
          'A word counter counts the number of words, characters, sentences, and paragraphs in your text. It updates in real time as you type or paste. Useful for essays, articles, social posts, and any content with length limits. Focuzon’s tool shows words, characters (with and without spaces), sentences, and paragraphs. No signup required. Runs in your browser.',
      },
      {
        question: 'Is the word counter free?',
        answer: 'Yes. You can use it for free with no signup or limits. No credit card or account required. Count as much text as you need. The tool works immediately in your browser. Focuzon offers it with other free productivity tools. Useful for students, writers, and professionals. No ads or usage caps. Your text stays private.',
      },
      {
        question: 'Does it count characters with spaces?',
        answer: 'Yes. The tool shows both total characters (including spaces) and characters without spaces. Some platforms count with spaces (e.g. Twitter), others without. Having both helps you meet different requirements. The count updates as you type. Useful for meta descriptions, headlines, or any character-limited field. No signup or limits.',
      },
      {
        question: 'Can I use it for essays?',
        answer: 'Yes. Many students use it to meet essay or assignment word limits. Paste your draft and see the count instantly. Adjust your writing to hit the required range. The tool also helps with character limits for abstracts or titles. No signup required. Your text is not sent to any server; it stays in your browser.',
      },
      {
        question: 'Is my text stored?',
        answer: 'The counter runs entirely in your browser. Your text is not sent to or stored on any server. Nothing is logged or saved. When you close the tab, the content is gone. This is important for confidential drafts, personal notes, or sensitive content. Focuzon does not collect or process the text you paste.',
      },
    ],
    howToSchemaSteps: [
      { name: 'Paste or type text', text: 'Paste or type your text in the box.' },
      { name: 'View counts', text: 'See live word, character, sentence, and paragraph counts.' },
    ],
  },
  'unit-converter': {
    title: 'Free Unit Converter – Length, Weight, Temperature, Volume | Focuzon',
    metaDescription:
      'Convert length, weight, temperature, and volume. Free unit converter: cm to inch, kg to lb, Celsius to Fahrenheit. No signup required.',
    ogTitle: 'Free Unit Converter | Focuzon',
    ogDescription: 'Convert length, weight, temperature, volume. Free and no signup.',
    h1: 'Unit Converter',
    subtitle: 'Convert length, weight, temperature, and volume.',
    whatIs:
      'A unit converter converts between units in a category, for example length (cm to inch), weight (kg to lb), temperature (Celsius to Fahrenheit), or volume. You enter a value and see the result in the unit you choose.',
    howToSteps: [
      'Choose a category (length, weight, temperature, volume)',
      'Select from and to units',
      'Enter a value',
      'See the converted result',
    ],
    features: [
      'Length, weight, temperature, volume',
      'Common units in each category',
      'Accurate conversion factors',
      'No signup required',
      'Works on any device',
    ],
    benefits:
      'Converting units quickly is useful for recipes, travel, DIY, or work. This tool gives you the answer without opening multiple sites.',
    faqs: [
      {
        question: 'What is a unit converter?',
        answer:
          'A unit converter converts a value from one unit to another in the same category. Examples: cm to inches, kg to pounds, Celsius to Fahrenheit, liters to gallons. You enter a value and select from and to units; the result appears instantly. Useful for recipes, DIY, travel, or work. Focuzon\'s tool is free with no signup.',
      },
      {
        question: 'Is the unit converter free?',
        answer: 'Yes. You can use it for free with no signup or limits. No credit card or account required. Convert as often as you need. The tool works in your browser. Focuzon offers it with other free productivity tools. Supports length, weight, temperature, and volume. Works on desktop and mobile.',
      },
      {
        question: 'Can I convert cm to inches?',
        answer: 'Yes. Choose the length category and select cm as “from” and inch as “to”, then enter your value.',
      },
      {
        question: 'Does it support temperature?',
        answer: 'Yes. You can convert between Celsius, Fahrenheit, and Kelvin. Select the temperature category and choose from and to units. Enter the value and see the result. Useful for cooking, weather, or science. Conversion formulas are standard and accurate. The tool also supports length, weight, and volume. Free with no signup.',
      },
      {
        question: 'Which categories are supported?',
        answer: 'The tool supports length (cm, inch, km, mile, m, foot), weight (kg, lb, g, oz), temperature (Celsius, Fahrenheit, Kelvin), and volume (liters, US gallons, ml). Common units in each category are included. Use the dropdowns to select from and to units. Direct URL shortcuts exist for pairs like cm-to-inch or kg-to-lb.',
      },
    ],
    howToSchemaSteps: [
      { name: 'Choose category', text: 'Select length, weight, temperature, or volume.' },
      { name: 'Select units', text: 'Choose from and to units (e.g. cm to inch).' },
      { name: 'Enter value', text: 'Enter the value and see the converted result.' },
    ],
  },
};

export function getToolSEO(toolId: string): ToolSEOConfig | null {
  return SEO[toolId] ?? null;
}

export function getToolCanonicalUrl(toolId: string, slug?: string): string {
  if (slug) return `${BASE_URL}/tools/${toolId}/${slug}`;
  return `${BASE_URL}/tools/${toolId}`;
}

export function getToolFAQSchema(faqs: ToolFAQ[]) {
  return faqSchema(
    faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    }))
  );
}

export function getToolSoftwareApplicationSchema(toolId: string, name: string, description: string) {
  return softwareApplicationSchema(toolId, name, description);
}

export function getToolHowToSchema(name: string, steps: { name: string; text: string }[]) {
  return howToSchema(name, steps);
}

export { BASE_URL };
